import { useState, useCallback } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
    TourDemoType,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
    demo?: TourDemoType
}

const TOUR_STEPS: TourStep[] = [
    // Credit Summary Card
    {
        selector: '[data-tour="credit-summary"]',
        title: 'Tổng Quan Ký Quỹ',
        description:
            'Hiển thị tổng quan về ví ký quỹ của khách hàng:\n• Số dư khả dụng / Tổng tiền\n• Số tiền đã sử dụng\n• Thanh progress bar cho biết % còn lại',
    },
    {
        selector: '[data-tour="credit-remaining"]',
        title: 'Số Dư Khả Dụng',
        description:
            'Số tiền ký quỹ còn lại có thể sử dụng:\n• Màu xanh: còn nhiều (≥70%)\n• Màu cam: trung bình (30-70%)\n• Màu đỏ: sắp hết (<30%)',
    },
    {
        selector: '[data-tour="credit-used"]',
        title: 'Đã Sử Dụng',
        description:
            'Tổng số tiền ký quỹ đã được sử dụng để thanh toán các hóa đơn.',
    },
    {
        selector: '[data-tour="credit-wallet-select"]',
        title: 'Chọn Ví Thanh Toán',
        description:
            'Lọc theo ví ký quỹ cụ thể:\n• "Tất cả": Xem tổng hợp tất cả các ví\n• Chọn ví cụ thể để xem chi tiết từng ví',
    },
    {
        selector: '[data-tour="credit-actions"]',
        title: 'Thao Tác Ký Quỹ',
        description:
            'Các thao tác với ví ký quỹ:\n• Thêm/Hoàn Tiền: Nạp thêm tiền vào ví\n• Chỉnh Sửa: Sửa thông tin ví (khi chọn ví cụ thể)',
        demo: 'credit-transaction-modal',
    },
    // Invoices section
    {
        selector: '[data-tour="invoices-merge"]',
        title: 'Gộp Hóa Đơn',
        description:
            'Gộp nhiều hóa đơn thành một:\n• Chọn ít nhất 2 hóa đơn bằng checkbox\n• Click "Gộp hóa đơn" để thực hiện\n• Chỉ gộp được hóa đơn cùng trạng thái',
    },
    {
        selector: '[data-tour="invoices-list"]',
        title: 'Danh Sách Hóa Đơn',
        description:
            'Danh sách tất cả hóa đơn của khách hàng:\n• Click vào mã hóa đơn để xem chi tiết\n• Sử dụng checkbox để chọn nhiều hóa đơn\n• Xem trạng thái, số tiền, ngày tạo',
    },
    {
        selector: '[data-tour="invoices-statistical"]',
        title: 'Thống Kê Hóa Đơn',
        description:
            'Tổng quan thống kê hóa đơn:\n• Số lượng hóa đơn theo trạng thái\n• Tổng doanh thu\n• Tình trạng thanh toán',
    },
    // Credit transactions
    {
        selector: '[data-tour="credit-transactions"]',
        title: 'Lịch Sử Giao Dịch Ký Quỹ',
        description:
            'Danh sách các giao dịch ký quỹ:\n• Nạp tiền, hoàn tiền\n• Thanh toán hóa đơn từ ví\n• Chi tiết từng giao dịch',
    },
]

export const useCustomerInvoicesTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)

    const getVisibleSteps = useCallback(() => {
        return TOUR_STEPS.filter((step) => {
            const element = document.querySelector(step.selector)
            return element !== null
        })
    }, [])

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentStep(0)
    }, [])

    const showTourStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const step = steps[stepIndex]
            if (!step) return

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                // Skip to next step if element not found
                if (stepIndex < steps.length - 1) {
                    showTourStep(stepIndex + 1, steps)
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const isLastStep = stepIndex === steps.length - 1
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: steps.length,
                    onPrev:
                        stepIndex > 0
                            ? () => goToStep(stepIndex - 1, steps)
                            : undefined,
                    onNext: isLastStep
                        ? endTour
                        : () => goToStep(stepIndex + 1, steps),
                    onClose: endTour,
                    isLastStep,
                    demo: step.demo,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        },
        [endTour]
    )

    const goToStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            setCurrentStep(stepIndex)
            showTourStep(stepIndex, steps)
        },
        [showTourStep]
    )

    const startTour = useCallback(() => {
        const visibleSteps = getVisibleSteps()
        if (visibleSteps.length === 0) {
            console.warn('No visible elements to tour')
            return
        }

        setIsActive(true)
        setCurrentStep(0)
        showTourStep(0, visibleSteps)
    }, [getVisibleSteps, showTourStep])

    return {
        isActive,
        currentStep,
        startTour,
        endTour,
        totalSteps: getVisibleSteps().length,
    }
}

export default useCustomerInvoicesTour
